import React from "react";
import { FaShieldAlt, FaLock, FaCookie, FaUserSecret } from "react-icons/fa";

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: <FaShieldAlt />,
      title: "Our Commitment to Your Privacy",
      content: `At CabandGo, we take your privacy seriously. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.`,
    },
    {
      icon: <FaLock />,
      title: "Information We Collect",
      content: `We collect information you provide directly to us, such as when you create an account, request a ride, contact support, or update your profile. This includes:
        • Name, phone number, email address
        • Pickup and drop-off locations
        • Payment information (processed securely via encrypted channels)
        • Profile photo and vehicle details (for drivers)
        • Device information and location data (with your permission)`,
    },
    {
      icon: <FaCookie />,
      title: "How We Use Your Information",
      content: `We use your information to:
        • Provide, maintain, and improve our services
        • Match riders with drivers efficiently
        • Process payments and send receipts
        • Send important updates and promotional offers (you can opt out)
        • Detect and prevent fraud or abuse
        • Comply with legal obligations`,
    },
    {
      icon: <FaUserSecret />,
      title: "Data Protection & Security",
      content: `We implement industry-standard security measures including:
        • End-to-end encryption for sensitive data
        • Secure payment processing (PCI DSS compliant)
        • Regular security audits and penetration testing
        • Limited employee access on a need-to-know basis
        • Two-factor authentication available for accounts`,
    },
    {
      icon: <FaShieldAlt />,
      title: "Sharing Your Information",
      content: `We do not sell your personal data. We only share information when:
        • Matching you with a driver/rider (name, phone, location)
        • Processing payments with trusted partners
        • Required by law or to protect safety
        • With your explicit consent`,
    },
    {
      icon: <FaLock />,
      title: "Your Rights & Choices",
      content: `You have full control over your data. You can:
        • Access and download your data anytime
        • Correct inaccurate information
        • Request deletion of your account and data
        • Opt out of marketing communications
        • Disable location sharing when not using the app`,
    },
  ];

  return (
    <div
      className="privacy-policy-page"
      style={{
        backgroundColor: "#fffdf5",
        minHeight: "100vh",
        padding: "80px 20px",
      }}
    >
      <style jsx>{`
        .container {
          max-width: 1100px;
          margin: 0 auto;
        }
        .header {
          text-align: center;
          margin-bottom: 70px;
        }
        .title {
          font-size: 48px;
          font-weight: 900;
          color: #000;
          margin-bottom: 16px;
        }
        .subtitle {
          font-size: 22px;
          color: #098535;
          font-weight: 600;
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
        }
        .last-updated {
          color: #666;
          font-size: 16px;
          margin-top: 20px;
        }
        .highlight {
          color: #098535;
          background: #fede33;
          padding: 4px 12px;
          border-radius: 8px;
          font-weight: 800;
        }

        .section-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
          gap: 32px;
          margin-top: 40px;
        }
        .section-card {
          background: white;
          border-radius: 20px;
          padding: 36px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.07);
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
        }
        .section-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
        }
        .icon-wrapper {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #fede33, #ffd700);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          color: #098535;
          font-size: 36px;
          box-shadow: 0 8px 25px rgba(254, 222, 51, 0.4);
        }
        .section-title {
          font-size: 24px;
          font-weight: 800;
          color: #000;
          margin-bottom: 18px;
        }
        .section-content {
          color: #444;
          line-height: 1.8;
          font-size: 16.5px;
        }
        .section-content ul {
          padding-left: 20px;
          margin: 16px 0;
        }
        .section-content li {
          margin-bottom: 10px;
          position: relative;
        }
        .section-content li:before {
          content: "✓";
          color: #098535;
          font-weight: bold;
          position: absolute;
          left: -20px;
        }

        /* Updated Contact Box - Black with White Text */
        .contact-box {
          background: #000000 !important; /* Black background */
          color: white !important; /* White text */
          border-radius: 20px;
          padding: 50px;
          text-align: center;
          margin: 80px auto;
          max-width: 900px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5);
        }
        .contact-title {
          font-size: 32px;
          font-weight: 900;
          margin-bottom: 16px;
          color: white !important; /* White text */
        }
        .contact-text {
          font-size: 18px;
          opacity: 0.95;
          line-height: 1.7;
          color: rgba(
            255,
            255,
            255,
            0.95
          ) !important; /* White text with slight transparency */
        }
        .email {
          font-size: 22px;
          font-weight: bold;
          color: #fede33 !important; /* Yellow email */
          margin-top: 20px;
          text-decoration: none;
          display: inline-block;
          transition: all 0.3s ease;
        }
        .email:hover {
          color: #ffffff !important; /* White on hover */
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .title {
            font-size: 38px;
          }
          .subtitle {
            font-size: 19px;
          }
          .section-grid {
            grid-template-columns: 1fr;
          }
          .section-card {
            padding: 28px;
          }
          .icon-wrapper {
            width: 70px;
            height: 70px;
            font-size: 32px;
          }
          .contact-box {
            padding: 40px 25px;
          }
          .contact-title {
            font-size: 28px;
          }
        }
      `}</style>

      <div className="container">
        {/* Header */}
        <div className="header">
          <h1 className="title">
            Privacy <span className="highlight">Policy</span>
          </h1>
          <p className="subtitle">
            Your trust is our top priority. We protect your data with bank-level
            security and never sell your personal information.
          </p>
          {/* <div className="last-updated">
            Last updated: December 1, 2025
          </div> */}
        </div>

        {/* Main Sections */}
        <div className="section-grid">
          {sections.map((section, index) => (
            <div key={index} className="section-card">
              <div className="icon-wrapper">{section.icon}</div>
              <h3 className="section-title">{section.title}</h3>
              <div className="section-content">
                {section.title.includes("Information We Collect") ||
                section.title.includes("How We Use") ||
                section.title.includes("Your Rights") ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: section.content.replace(/\•/g, "<br/>• "),
                    }}
                  />
                ) : (
                  <p>{section.content}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section - Now Black with White Text */}
        <div className="contact-box">
          <h3 className="contact-title">Questions About Your Privacy?</h3>
          <p className="contact-text">
            We're here to help. If you have any concerns about your data or this
            policy, our dedicated privacy team is ready to assist you 24/7.
          </p>
          <a href="mailto:privacy@cabandgo.com" className="email">
            privacy@cabandgo.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
